import React, { useState, useEffect } from 'react';
import AdminDashboard from './AdminDashboard';
import Swal from 'sweetalert2';
import '../../assets/css/admin/allcourses.css';

const DeleteStudents = () => {
    const [students, setStudents] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredStudents, setFilteredStudents] = useState([]);

    useEffect(() => {
        fetchDeletedStudents();
    }, []);

    const fetchDeletedStudents = async () => {
        try {
            const response = await fetch('https://api.myskills.lk/std/deleted');
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const data = await response.json();
            setStudents(data);
            setFilteredStudents(data); // Initialize filteredStudents with all data
        } catch (error) {
            console.error('Error fetching deleted students:', error);
        }
    };

    // Real-time filtering effect
    useEffect(() => {
        if (!searchTerm.trim()) {
            // If search is empty, show all deleted students
            setFilteredStudents(students);
            return;
        }
        
        // Filter students based on multiple fields
        const filtered = students.filter(student => 
            (student.name && student.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.address && student.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.district && student.district.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.province && student.province.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.phone && student.phone.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.mobile && student.mobile.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.email && student.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.school && student.school.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.course && student.course.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (student.category && student.category.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        
        setFilteredStudents(filtered);
    }, [searchTerm, students]);

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleRestore = async (studentId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "Do you want to restore this student?",
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, restore it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`https://api.myskills.lk/std/restore/${studentId}`, {
                        method: 'POST'
                    });
                    if (response.ok) {
                        const updatedStudents = students.filter(student => student.id !== studentId);
                        setStudents(updatedStudents);
                        setFilteredStudents(updatedStudents.filter(student => 
                            !searchTerm || 
                            (student.name && student.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            // Include other search conditions as needed
                            (student.category && student.category.toLowerCase().includes(searchTerm.toLowerCase()))
                        ));
                        Swal.fire({
                            title: "Restored!",
                            text: "The student has been restored.",
                            icon: "success"
                        });
                    } else {
                        console.error('Failed to restore student');
                        Swal.fire({
                            title: "Error!",
                            text: "Failed to restore the student.",
                            icon: "error"
                        });
                    }
                } catch (error) {
                    console.error('Error restoring student:', error);
                    Swal.fire({
                        title: "Error!",
                        text: "Failed to restore the student.",
                        icon: "error"
                    });
                }
            }
        });
    };

    const handlePermanentDelete = async (studentId) => {
        Swal.fire({
            title: "Are you sure?",
            text: "This will permanently delete the student. This action cannot be undone!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete permanently!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await fetch(`https://api.myskills.lk/std/delete/permanent/${studentId}`, {
                        method: 'DELETE'
                    });
                    if (response.ok) {
                        const updatedStudents = students.filter(student => student.id !== studentId);
                        setStudents(updatedStudents);
                        setFilteredStudents(updatedStudents.filter(student => 
                            !searchTerm || 
                            (student.name && student.name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            // Include other search conditions as needed
                            (student.category && student.category.toLowerCase().includes(searchTerm.toLowerCase()))
                        ));
                        Swal.fire({
                            title: "Deleted!",
                            text: "The student has been permanently deleted.",
                            icon: "success"
                        });
                    } else {
                        console.error('Failed to delete student permanently');
                        Swal.fire({
                            title: "Error!",
                            text: "Failed to permanently delete the student.",
                            icon: "error"
                        });
                    }
                } catch (error) {
                    console.error('Error deleting student permanently:', error);
                    Swal.fire({
                        title: "Error!",
                        text: "Failed to permanently delete the student.",
                        icon: "error"
                    });
                }
            }
        });
    };

    return (
        <div>
            <AdminDashboard />
            <div className='AllViews-admin-course'>
                <div className="search-icon-admin admin-course-search">
                    <h1>DELETE STUDENTS</h1>
                    <form>
                        <input 
                            type="text" 
                            value={searchTerm}
                            onChange={handleSearch}
                            placeholder='Search Student' 
                        />
                        <input type="submit" value="SEARCH" onClick={(e) => e.preventDefault()} />
                    </form>
                </div>
                <div className="scroller-box">
                    <div className="table-course">
                        <table>
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Address</th>
                                    <th>District</th>
                                    <th>Province</th>
                                    <th>Phone</th>
                                    <th>Mobile</th>
                                    <th>Email</th>
                                    <th>School</th>
                                    <th>Course</th>
                                    <th>Category</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredStudents.length > 0 ? (
                                    filteredStudents.map((student) => (
                                        <tr key={student.id}>
                                            <td>{student.name}</td>
                                            <td>{student.address}</td>
                                            <td>{student.district}</td>
                                            <td>{student.province}</td>
                                            <td>{student.phone}</td>
                                            <td>{student.mobile}</td>
                                            <td>{student.email}</td>
                                            <td>{student.school}</td>
                                            <td>{student.course}</td>
                                            <td>{student.category}</td>
                                            <td id='action-buttons'>
                                                <a href="#!" id='restore-details' onClick={() => handleRestore(student.id)}>RESTORE</a>
                                                <a href="#!" id='del-details' onClick={() => handlePermanentDelete(student.id)}>DELETE</a>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="11" style={{textAlign: 'center'}}>No deleted students found matching your search</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteStudents;
